import React from 'react';
import {
    PrimaryButton,
    Stack,
    TextField,
    Text,
    Separator,
    MessageBar,
    MessageBarType,
    Label,
    FontWeights,
    getTheme,
    mergeStyleSets,
} from '@fluentui/react';
import { Car } from '../Model/Car';
import { LogbookEntry } from '../Model/LogbookEntry';
import { useLogbookStore, TripPurpose } from '../store/logbookStore';
import { Rate, getApiService } from '../services/ApiService';

import CarSelector from './CarSelector';
import PurposeSelector from './PurposeSelector';
import RateSelector from './RateSelector';
import ConfirmationDialog from './ConfirmationDialog';

const LAST_CAR_KEY = 'taxisever_last_car_id';

interface FormProps {
    sendToDatabase(logbookEntry: LogbookEntry): unknown;
    cars: Car[];
}

const Form: React.FC<FormProps> = ({ sendToDatabase, cars }) => {
    const theme = getTheme();
    const [selectedCar, setSelectedCar] = React.useState<Car | null>(null);
    const [showDialog, setShowDialog] = React.useState(false);
    const [kmInput, setKmInput] = React.useState('');
    const [kmError, setKmError] = React.useState('');
    const [rates, setRates] = React.useState<Rate[]>([]);

    const {
        selectedCarId,
        setSelectedCarId,
        purpose,
        setPurpose,
        rateType,
        setRateType,
        deposit,
        setDeposit,
        cardPayment,
        setCardPayment,
        additionalExpense,
        setAdditionalExpense,
        previouslyAgreedPrice,
        setPreviouslyAgreedPrice,
        personalWriteoff,
        setPersonalWriteoff,
        companyWriteoff,
        setCompanyWriteoff,
        generalWriteoff,
        setGeneralWriteoff,
        isSubmitting,
        setIsSubmitting,
        submitError,
        setSubmitError,
        resetForm,
    } = useLogbookStore();

    const previousKm = selectedCar?.kilometers ?? 0;
    const currentKm = parseInt(kmInput, 10) || 0;
    const kmBillable = Math.max(0, currentKm - previousKm);

    const selectedRate = rates.find(r => r.id === purpose) ?? null;
    const isTaxi = selectedRate?.rateType === 'Taxi';
    const isService = selectedRate?.rateType === 'Service';
    const isPersonal = selectedRate?.rateType === 'Personal';
    const isContract = selectedRate?.rateType === 'Contract';
    const showRate = isTaxi;
    const showDeposit = isTaxi;

    const rate = rateType === 'night'
        ? (selectedRate?.nightRate ?? 0)
        : rateType === 'day'
            ? (selectedRate?.dayRate ?? 0)
            : 0;
    const taxiNetKm = Math.max(0, kmBillable - (personalWriteoff || 0) - (companyWriteoff || 0) - (generalWriteoff || 0));

    let finalPrice: number;
    switch (selectedRate?.rateType) {
        case 'Taxi':
            finalPrice = Math.round((taxiNetKm * rate) + ((generalWriteoff || 0) * 3) + (deposit || 0) - (cardPayment || 0) - (additionalExpense || 0)
            );
            break;
        case 'Service':
            finalPrice = -(additionalExpense || 0);
            break;
        case 'Personal':
            finalPrice = Math.round(kmBillable * 7 - (additionalExpense || 0));
            break;
        case 'Contract':
            finalPrice = Math.round(previouslyAgreedPrice * 2 / 3 + (deposit || 0) - (cardPayment || 0) - (additionalExpense || 0));
            break;
        default:
            finalPrice = 0;
    }

    const canSubmit =
        !!selectedCarId &&
        !!purpose &&
        (!showRate || !!rateType) &&
        !!kmInput &&
        currentKm > previousKm;

    // Load rates from API on mount
    React.useEffect(() => {
        getApiService().getRates()
            .then(setRates)
            .catch(() => { /* rates will stay empty; form shows no purposes */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Restore last used car on mount
    React.useEffect(() => {
        const lastCarId = localStorage.getItem(LAST_CAR_KEY);
        if (lastCarId && cars.length > 0) {
            const car = cars.find(c => c.rowKey === lastCarId);
            if (car) {
                setSelectedCar(car);
                setSelectedCarId(lastCarId);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cars]);

    const handleCarSelect = (car: Car | null, carId: string) => {
        setSelectedCar(car);
        setSelectedCarId(carId);
        if (carId) localStorage.setItem(LAST_CAR_KEY, carId);
    };

    const handlePurposeSelect = (p: TripPurpose | null) => {
        const carId = selectedCarId;
        resetForm();
        setSelectedCarId(carId);
        setPurpose(p);
    };

    const handleRateSelect = (selectedRateType: 'day' | 'night' | '') => {
        setRateType(selectedRateType);
    };

    const handleKmChange = (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
        const v = value ?? '';
        setKmInput(v);
        const parsed = parseInt(v, 10);
        if (v && (!Number.isInteger(parsed) || parsed <= previousKm)) {
            setKmError(`Musí být větší než ${previousKm.toLocaleString('cs-CZ')} km`);
        } else {
            setKmError('');
        }
    };

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            setSubmitError(null);

            const entry: LogbookEntry = {
                id: 'placeholder',
                envelope: 'placeholder',
                carId: selectedCarId,
                kilometersState: currentKm,
                kilometersBillable: kmBillable,
                kilometersPersonalWriteoff: personalWriteoff || 0,
                kilometersCompanyWriteoff: companyWriteoff || 0,
                kilometersGeneralWriteoff: generalWriteoff || 0,
                additionalExpenses: additionalExpense || 0,
                previouslyAgreedPrice: previouslyAgreedPrice ?? null,
                cardPayments: cardPayment || 0,
                depositAmount: deposit || null,
                rowChecked: false,
                driverId: 'placeholder',
                purpose,
                rate,
                timestamp: new Date(),
                finalPrice,
            };

            await sendToDatabase(entry);
            resetForm();
            setKmInput('');
            setKmError('');
            setSelectedCar(null);
            setShowDialog(false);
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : 'Chyba při odesílání');
        } finally {
            setIsSubmitting(false);
        }
    };

    const styles = mergeStyleSets({
        section: { marginBottom: 20 },
        row: { display: 'flex', gap: '16px', flexWrap: 'wrap' as const },
        col: { flex: '1 1 200px', minWidth: 0 },
        prevKm: {
            background: theme.palette.neutralLighterAlt,
            border: `1px solid ${theme.palette.neutralLight}`,
            borderRadius: 6,
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 28,
        },
        priceBar: {
            position: 'sticky' as const,
            bottom: 0,
            zIndex: 10,
            background: theme.palette.themeLighterAlt,
            border: `2px solid ${theme.palette.themePrimary}`,
            borderRadius: 8,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 8,
        },
        priceCaption: { color: theme.palette.neutralSecondary, fontSize: 13 },
        priceValue: {
            color: theme.palette.themePrimary,
            fontWeight: FontWeights.bold,
            fontSize: 28,
        },
    });

    const mobileDropdownStyles = {
        title: { height: 44, lineHeight: '42px' },
        caretDownWrapper: { height: 44, lineHeight: '44px' },
    };

    return (
        <Stack tokens={{ childrenGap: 0 }}>

            {/* ── Car + Purpose ─────────────────────────────────────── */}
            <div className={styles.section}>
                <div className={styles.row}>
                    <div className={styles.col}>
                        <CarSelector
                            cars={cars}
                            selectedCarId={selectedCarId}
                            onCarSelect={handleCarSelect}
                            dropdownStyles={mobileDropdownStyles}
                        />
                    </div>
                    <div className={styles.col}>
                        <PurposeSelector
                            purpose={purpose}
                            rates={rates}
                            onPurposeChange={handlePurposeSelect}
                            dropdownStyles={mobileDropdownStyles}
                        />
                    </div>
                </div>
            </div>

            {/* ── Rate selector ─────────────────────────────────────── */}
            {showRate && (
                <div className={styles.section}>
                    <RateSelector
                        rateType={rateType as 'day' | 'night' | ''}
                        selectedRate={selectedRate}
                        onRateChange={handleRateSelect}
                        showRateDisplay={true}
                    />
                </div>
            )}

            {/* ── Odometer ──────────────────────────────────────────── */}
            {selectedCar ? (
                <div className={styles.section}>
                    <div className={styles.row}>
                        <div className={styles.col}>
                            <div className={styles.prevKm}>
                                <Text variant="small" style={{ color: theme.palette.neutralSecondary }}>
                                    Aktuální stav počitadla
                                </Text>
                                <Text variant="large" style={{ fontWeight: FontWeights.bold }}>
                                    {previousKm.toLocaleString('cs-CZ')} km
                                </Text>
                            </div>
                        </div>
                        <div className={styles.col}>
                            <TextField
                                label="Nový stav počitadla"
                                placeholder={`Více než ${previousKm.toLocaleString('cs-CZ')}`}
                                value={kmInput}
                                onChange={handleKmChange}
                                type="text"
                                errorMessage={kmError}
                                suffix="km"
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className={styles.section}>
                    <MessageBar messageBarType={MessageBarType.info}>
                        Nejprve vyberte vozidlo pro zadání kilometrů.
                    </MessageBar>
                </div>
            )}

            {/* ── Taxi: write-offs ──────────────────────────────────── */}
            {isTaxi && (
                <>
                    <Label style={{ color: theme.palette.neutralSecondary, marginBottom: 4 }}>
                        Odpisy km
                    </Label>
                    <div className={styles.section}>
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <TextField
                                    label="Soukromý odpis (5 km z každé směny)"
                                    value={personalWriteoff?.toString() ?? ''}
                                    onChange={(_, v) => setPersonalWriteoff(parseFloat(v || '0'))}
                                    type="number"
                                    suffix="km"
                                    min={0}
                                />
                            </div>
                            <div className={styles.col}>
                                <TextField
                                    label="Firemní odpis (tankování, myčka...)"
                                    value={companyWriteoff?.toString() ?? ''}
                                    onChange={(_, v) => setCompanyWriteoff(parseFloat(v || '0'))}
                                    type="number"
                                    suffix="km"
                                    min={0}
                                />
                            </div>
                            <div className={styles.col}>
                                <TextField
                                    label="Marný odpis (nezaplacené jízdy)"
                                    value={generalWriteoff?.toString() ?? ''}
                                    onChange={(_, v) => setGeneralWriteoff(parseFloat(v || '0'))}
                                    type="number"
                                    suffix="km"
                                    min={0}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Taxi: card payment + additional expenses + deposit ── */}
                    <Label style={{ color: theme.palette.neutralSecondary, marginBottom: 4 }}>
                        Platby a náklady
                    </Label>
                    <div className={styles.section}>
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <TextField
                                    label="Platba kartou"
                                    value={cardPayment?.toString() ?? ''}
                                    onChange={(_, v) => setCardPayment(parseFloat(v || '0'))}
                                    type="number"
                                    suffix="Kč"
                                    min={0}
                                />
                            </div>
                            <div className={styles.col}>
                                <TextField
                                    label="Tankování a další náklady"
                                    value={additionalExpense?.toString() ?? ''}
                                    onChange={(_, v) => setAdditionalExpense(parseFloat(v || '0'))}
                                    type="number"
                                    suffix="Kč"
                                    min={0}
                                />
                            </div>
                            {showDeposit && (
                                <div className={styles.col}>
                                    <TextField
                                        label="Záloha"
                                        value={deposit?.toString() ?? ''}
                                        onChange={(_, v) => setDeposit(parseFloat(v || '0'))}
                                        type="number"
                                        suffix="Kč"
                                        min={0}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* ── Service: additional expenses only ─────────────────── */}
            {isService && (
                <div className={styles.section}>
                    <TextField
                        label="Tankování a další náklady"
                        value={additionalExpense?.toString() ?? ''}
                        onChange={(_, v) => setAdditionalExpense(parseFloat(v || '0'))}
                        type="number"
                        suffix="Kč"
                        min={0}
                    />
                </div>
            )}

            {/* ── Personal: additional expenses ─────────────────────── */}
            {isPersonal && (
                <div className={styles.section}>
                    <TextField
                        label="Další náklady"
                        value={additionalExpense?.toString() ?? ''}
                        onChange={(_, v) => setAdditionalExpense(parseFloat(v || '0'))}
                        type="number"
                        suffix="Kč"
                        min={0}
                    />
                </div>
            )}

            {/* ── Contract: agreed price + payments ───────────────── */}
            {isContract && (
                <div className={styles.section}>
                    <div style={{ marginBottom: 12 }}>
                        <TextField
                            label="Dohodnutá cena"
                                value={previouslyAgreedPrice?.toString() ?? '0'}
                                onChange={(_, v) => setPreviouslyAgreedPrice(v ? parseFloat(v) : 0)}
                            type="number"
                            suffix="Kč"
                            min={0}
                        />
                    </div>
                    <div className={styles.row}>
                        <div className={styles.col}>
                            <TextField
                                label="Záloha"
                                value={deposit?.toString() ?? ''}
                                onChange={(_, v) => setDeposit(parseFloat(v || '0'))}
                                type="number"
                                suffix="Kč"
                                min={0}
                            />
                        </div>
                        <div className={styles.col}>
                            <TextField
                                label="Platba kartou"
                                value={cardPayment?.toString() ?? ''}
                                onChange={(_, v) => setCardPayment(parseFloat(v || '0'))}
                                type="number"
                                suffix="Kč"
                                min={0}
                            />
                        </div>
                        <div className={styles.col}>
                            <TextField
                                label="Další náklady"
                                value={additionalExpense?.toString() ?? ''}
                                onChange={(_, v) => setAdditionalExpense(parseFloat(v || '0'))}
                                type="number"
                                suffix="Kč"
                                min={0}
                            />
                        </div>
                    </div>
                </div>
            )}

            <Separator />

            {/* ── Errors ────────────────────────────────────────────── */}
            {submitError && (
                <div style={{ marginBottom: 12 }}>
                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setSubmitError(null)}>
                        {submitError}
                    </MessageBar>
                </div>
            )}

            {/* ── Price bar + Submit ─────────────────────────────────── */}
            <div className={styles.priceBar}>
                <div>
                    <div className={styles.priceCaption}>
                        {isTaxi && (finalPrice >= 0 ? 'Do obálky vložíš:' : 'Dostaneš:')}
                        {isService && (finalPrice >= 0 ? 'Do obálky vložíš:' : 'Dostaneš:')}
                        {isPersonal && (finalPrice >= 0 ? 'Do obálky vložíš:' : 'Dostaneš:')}
                        {isContract && (finalPrice >= 0 ? 'Do obálky vložíš:' : 'Dostaneš:')}
                    </div>
                    {selectedRate && (
                        <div className={styles.priceValue}>
                            {Math.abs(finalPrice).toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Kč
                        </div>
                    )}
                </div>
                <PrimaryButton
                    text="Odeslat jízdu"
                    iconProps={{ iconName: 'Send' }}
                    disabled={!canSubmit || isSubmitting}
                    onClick={() => setShowDialog(true)}
                    styles={{ root: { height: 48, borderRadius: 6 } }}
                />
            </div>

            {/* ── Confirmation dialog ────────────────────────────────── */}
            <ConfirmationDialog
                isOpen={showDialog}
                title="Zkontroluj si informace"
                message={
                    <span>
                        Pokud věříš, že jsou informace správně, klikni &ldquo;Odeslat&rdquo;.{' '}
                        {finalPrice > 0
                            ? <>Do obálky dáš <strong>{finalPrice.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Kč</strong>.</>
                            : finalPrice < 0
                                ? <>Dostaneš <strong>{Math.abs(finalPrice).toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Kč</strong>.</>
                                : null
                        }
                    </span>
                }
                confirmText="Odeslat"
                cancelText="Zrušit"
                onConfirm={handleSubmit}
                onCancel={() => setShowDialog(false)}
                isSubmitting={isSubmitting}
            />
        </Stack>
    );
};

export default Form;
